import axios from "axios";
import { useEffect, useState } from "react";
import useGlobalContext from "../../hooks/useGlobalContext";
import localize from "../../common/localize";
import ModalBackground from "./ModalBackground";
import { Alert, Box, Typography } from "@mui/material";
import LoadingPanel from "./LoadingPanel";
import PrinterModal from "./PrinterModal";
import ColumnsSelector from "./ColumnsSelector";
import {
  GetDateInDDMMYYYYFormat,
  GetDateInMMDDYYYYFormat,
  getExpectedSolutionDateUI,
  getFieldTypeName,
  getOperationalStatusNameFromId,
  getPaymentTypeModeLabel,
  getStatusText,
  getValueInCurrency,
} from "../../common/utils";
import {
  getCurrencyFromSetup,
  getEventTotalValue,
  getEvidencesCount,
  getMovementTypeName,
  getNewnessByCode,
} from "../../common/EntitiesManagement";

export default function EntityPrinter({
  title,
  report_title,
  report_subtitle,
  url,
  filters,
  columns,
  closeCallback,
  objectTypeName,
}) {
  const { config, getHeadersWithCorrespondingPagination } = useGlobalContext();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showPrinter, setShowPrinter] = useState(false);
  const [message, setMessage] = useState("");
  const [messageTitle, setMessageTitle] = useState("");
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [processedColumns, setProcessedColumns] = useState([...columns]);
  const [showColumnsSelector, setShowColumnsSelector] = useState(false);
  const [showCsvSetup, setShowCsvSetup] = useState(false);
  const [separator, setSeparator] = useState(",");
  const [customSeparator, setCustomSeparator] = useState("");

  const getEffectiveSeparator = () => {
    if (separator === "custom") {
      return customSeparator || ",";
    }
    if (separator === "tab") return "\t";
    return separator;
  };

  const getData = async () => {
    setIsLoading(true);
    setMessage("");

    try {
      const result = await axios.get(
        url,
        getHeadersWithCorrespondingPagination(config, -1, filters),
      );

      if (result.data?.ok || result.data?.Ok) {
        setData(result.data?.Data);
        setShowColumnsSelector(true);
      }
    } catch (Exception) {
      console.error(
        "Error getting data: " + url + " Error: " + Exception.message,
      );
      setMessageTitle(localize.error_unexpected);
      setMessage(Exception.message);
      setShowErrorMessage(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const processDataAccordingToType = (entityData, isCsv) => {
    try {
      if (!entityData || entityData.length === 0) {
        return [[]];
      }

      return entityData.map((item_data) => {
        switch (objectTypeName) {
          case "additional_cost_type":
            return getAdditionalCostTypes(item_data);
          case "additional_newness":
            return getAdditionalNewness(item_data);
          case "client":
            return getClient(item_data);
          case "contractor":
            return getContractor(item_data);
          case "daily_route":
            return getDailyRoute(item_data);
          case "dynamic_form":
            return getDynamicForm(item_data);
          case "dynamic_form_field":
            return getDynamicFormField(item_data);
          case "event_status":
            return getExecutionStatus(item_data);
          case "event_type":
            return getEventType(item_data);
          case "evidence_template":
            return getEvidenceTemplate(item_data);
          case "inventory_movement_type":
            return getInventoryMovementType(item_data);
          case "inventory_movement_header":
            return getInventoryMovementHeader(item_data);
          case "measurement_unit":
            return getMeasurementUnit(item_data);
          case "operational_area":
            return getOperationalArea(item_data);
          case "payment_type":
            return getPaymentType(item_data);
          case "personal":
            return getPersonal(item_data);
          case "position":
            return getPosition(item_data);
          case "proceeding":
            return getProceeding(item_data);
          case "provider":
            return getProvider(item_data);
          case "request_type":
            return getRequestType(item_data);
          case "resolution":
            return getResolution(item_data);
          case "vehicle":
            return getVehicle(item_data);
          case "vehicle_type":
            return getVehicleType(item_data);
          case "warehouse":
            return getWarehouse(item_data);
          case "workflow":
            return getWorkflow(item_data);
          case "pre_order":
            return getPreOrder(item_data);
          case "newness":
            return getNewness(item_data);
          case "my_requests":
            return getMyRequests(item_data);
          case "requests_assigned_to_me":
            return getRequestsAssignedToMe(item_data);
          case "event":
            return getEvents(item_data, isCsv);
          case "related_bills":
            return getRelatedBills(item_data, isCsv);
          case "supply":
            return getSupplies(item_data);
          case "report_type":
            return getReportTypes(item_data);
        }
      });
    } catch (Exception) {
      console.error("Error processing data to print: ", Exception.message);
      return [];
    }
  };

  const getReportTypes = (item) => {
    const result = [];

    if (processedColumns.includes(localize.name)) {
      result.push(item.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.description)) {
      result.push(item.Description ?? localize.no_data);
    }

    if (processedColumns.includes(localize.edition_level)) {
      result.push(item.IsPublic ? localize.is_public : localize.is_private);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getSupplies = (item) => {
    const result = [];

    if (processedColumns.includes(localize.code)) {
      result.push(item.Code ?? localize.no_data);
    }

    if (processedColumns.includes(localize.name)) {
      result.push(item.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.quantity)) {
      result.push(item.Quantity?.toLocaleString() ?? "0");
    }

    if (processedColumns.includes(localize.measurement_unit)) {
      result.push(
        item.Unit?.Symbol && item.Unit?.Symbol !== ""
          ? `${item.Unit?.Name} (${item.Unit?.Symbol})`
          : `${item.Unit?.Name}`,
      );
    }

    if (processedColumns.includes(localize.provider)) {
      result.push(item.Provider?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getRelatedBills = (item, plainValue) => {
    const result = [];

    if (processedColumns.includes(localize.bill_code)) {
      result.push(item.Code ?? localize.no_data);
    }

    if (processedColumns.includes(localize.bill_date)) {
      result.push(GetDateInDDMMYYYYFormat(new Date(item.GenerationDate)));
    }

    if (processedColumns.includes(localize.client)) {
      result.push(
        item.Client
          ? `${item.Client.Identification} - ${item.Client.Name}`
          : localize.no_data,
      );
    }

    if (processedColumns.includes(localize.total_value)) {
      result.push(
        plainValue
          ? item.Value
          : getValueInCurrency(getCurrencyFromSetup(), item.Value ?? 0),
      );
    }

    if (processedColumns.includes(localize.observations)) {
      result.push(item.Observation ?? localize.no_data);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getEvents = (item, plainValue) => {
    const result = [];

    if (processedColumns.includes(localize.code)) {
      result.push(item.Code ?? localize.no_data);
    }

    if (processedColumns.includes(localize.event_type)) {
      result.push(item.EventType?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.operational_status)) {
      result.push(getOperationalStatusNameFromId(item.OperationalStatus));
    }

    if (processedColumns.includes(localize.execution_status)) {
      result.push(
        item.OperationalStatus === 0
          ? localize.cancelled
          : item.CurrentStatus?.Name ?? localize.no_data,
      );
    }

    if (processedColumns.includes(localize.proceeding)) {
      result.push(item.ProceedingInfo?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.assigned_to)) {
      result.push(
        item.PersonalAssigned?.SurName
          ? `${item.PersonalAssigned.SurName} ${item.PersonalAssigned.LastName}`
          : localize.no_data,
      );
    }

    if (processedColumns.includes(localize.total_value)) {
      result.push(
        plainValue
          ? getEventTotalValue(item, true)
          : getValueInCurrency(
              getCurrencyFromSetup(),
              getEventTotalValue(item, true),
            ),
      );
    }

    if (processedColumns.includes(localize.real_income)) {
      result.push(
        plainValue
          ? item.realIncome
          : getValueInCurrency(getCurrencyFromSetup(), item.realIncome),
      );
    }

    if (processedColumns.includes(localize.payed)) {
      result.push(item.Payed ? localize.yes : localize.no);
    }

    if (processedColumns.includes(localize.billed)) {
      result.push(item.Bill?.Billed ? localize.yes : localize.no);
    }

    if (processedColumns.includes(localize.expected_attention_date)) {
      result.push(getExpectedSolutionDateUI(item));
    }

    if (processedColumns.includes(localize.contractor)) {
      result.push(item.Contractor?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.client_name)) {
      result.push(item.Client?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.address)) {
      result.push(
        item.Address
          ? `${item.Address} ${item.AddressComplement ?? ""}`
          : item.Address ?? localize.no_data,
      );
    }

    if (processedColumns.includes(localize.department)) {
      result.push(item.State?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.city)) {
      result.push(item.City?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    if (processedColumns.includes(localize.creation_date)) {
      result.push(
        new Date(item.CreationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    return result;
  };

  const getMyRequests = (item) => {
    const result = [];

    if (processedColumns.includes(localize.type)) {
      result.push(item.Type?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.status)) {
      result.push(getStatusText(item.Status));
    }

    if (processedColumns.includes(localize.event)) {
      result.push(item.Event?.Code ?? localize.no_data);
    }

    if (processedColumns.includes(localize.client)) {
      result.push(item?.Client?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.assigned_to)) {
      result.push(
        `${item?.PersonalAssigned?.SurName} ${item?.PersonalAssigned?.LastName}`,
      );
    }

    if (processedColumns.includes(localize.expected_attention_date)) {
      result.push(
        GetDateInDDMMYYYYFormat(new Date(item?.ExpectedSolutionDate)),
      );
    }

    if (processedColumns.includes(localize.creation_date)) {
      result.push(
        new Date(item?.CreationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    return result;
  };

  const getRequestsAssignedToMe = (item) => {
    const result = [];

    if (processedColumns.includes(localize.type)) {
      result.push(item.Type?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.status)) {
      result.push(getStatusText(item.Status));
    }

    if (processedColumns.includes(localize.event)) {
      result.push(item.Event?.Code ?? localize.no_data);
    }

    if (processedColumns.includes(localize.client)) {
      result.push(item?.Client?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.expected_attention_date)) {
      result.push(
        GetDateInDDMMYYYYFormat(new Date(item?.ExpectedSolutionDate)),
      );
    }

    if (processedColumns.includes(localize.creation_date)) {
      result.push(
        new Date(item?.CreationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.created_by)) {
      result.push(item?.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getNewness = (item) => {
    const result = [];

    if (processedColumns.includes(localize.occurr_time)) {
      result.push(
        new Date(item.OccurrTime).toLocaleDateString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.newness)) {
      result.push(item.AdditionalNewness?.Name ?? getNewnessByCode(item.Code));
    }

    if (processedColumns.includes(localize.event)) {
      result.push(item.Event?.Code ?? localize.no_data);
    }

    if (processedColumns.includes(localize.reported_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    if (processedColumns.includes(localize.observations)) {
      result.push(item.Observation ?? localize.no_data);
    }

    return result;
  };

  const getPreOrder = (item) => {
    const result = [];

    if (processedColumns.includes(localize.pre_order)) {
      result.push(item.Code ?? localize.no_data);
    }

    if (processedColumns.includes(localize.status)) {
      result.push(
        item.Event
          ? item.Event?.CurrentStatus?.Name
          : item.CurrentStatus
          ? item.CurrentStatus?.Name
          : localize.quotation_status_pending,
      );
    }

    if (processedColumns.includes(localize.order_code)) {
      result.push(item.Event?.Code ?? localize.no_data);
    }

    if (processedColumns.includes(localize.client_name)) {
      result.push(item.ClientName ?? localize.no_data);
    }

    if (processedColumns.includes(localize.proceeding)) {
      result.push(item.ProductType?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.event_type)) {
      result.push(item.ProductBrand?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.reported_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    if (processedColumns.includes(localize.creation_date)) {
      result.push(
        new Date(item.CreationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.last_update)) {
      result.push(
        new Date(
          item.Event ? item.Event.ModificationDate : item.ModificationDate,
        ).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    return result;
  };

  const getWorkflow = (item) => {
    const result = [];

    if (processedColumns.includes(localize.name)) {
      result.push(item.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.description)) {
      result.push(item.Description ?? localize.no_data);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getWarehouse = (item) => {
    const result = [];

    if (processedColumns.includes(localize.name)) {
      result.push(item.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.description)) {
      result.push(item.Description ?? localize.no_data);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getVehicleType = (item) => {
    const result = [];

    if (processedColumns.includes(localize.name)) {
      result.push(item.Name ?? localize.name);
    }

    if (processedColumns.includes(localize.description)) {
      result.push(item.Description ?? localize.no_data);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy);
    }

    return result;
  };

  const getVehicle = (item) => {
    const result = [];

    if (processedColumns.includes(localize.vehicle_type)) {
      result.push(item.VehicleType?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.identification)) {
      result.push(item.Identification ?? localize.no_data);
    }

    if (processedColumns.includes(localize.soat)) {
      result.push(
        GetDateInMMDDYYYYFormat(new Date(item.Soat)) ?? localize.no_data,
      );
    }

    if (processedColumns.includes(localize.technomechanic_review)) {
      result.push(
        GetDateInMMDDYYYYFormat(new Date(item.TechnicalReview)) ??
          localize.no_data,
      );
    }

    if (processedColumns.includes(localize.assigned_to)) {
      result.push(
        item.AssignedTo
          ? `${item.AssignedTo?.SurName} ${item.AssignedTo?.LastName}`
          : localize.no_data,
      );
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getResolution = (item) => {
    const result = [];

    if (processedColumns.includes(localize.code)) {
      result.push(item.Code ?? localize.no_data);
    }

    if (processedColumns.includes(localize.name)) {
      result.push(item.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.description)) {
      result.push(item.Description ?? localize.no_data);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getRequestType = (item) => {
    const result = [];

    if (processedColumns.includes(localize.name)) {
      result.push(item.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.description)) {
      result.push(item.Description ?? localize.no_data);
    }

    if (processedColumns.includes(localize.background_color)) {
      result.push(item.BackgroundColor ?? localize.no_data);
    }

    if (processedColumns.includes(localize.foreground_color)) {
      result.push(item.ForegroundColor ?? localize.no_data);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getProvider = (item) => {
    const result = [];

    if (processedColumns.includes(localize.identification_type)) {
      result.push(item.IdentificationType ?? localize.no_data);
    }

    if (processedColumns.includes(localize.identification)) {
      result.push(item.Identification ?? localize.no_data);
    }

    if (processedColumns.includes(localize.name)) {
      result.push(item.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.phone_number)) {
      result.push(item.PhoneNumber ?? localize.no_data);
    }

    if (processedColumns.includes(localize.email)) {
      result.push(item.Email ?? localize.no_data);
    }

    if (processedColumns.includes(localize.contact_name)) {
      result.push(item.ContactName ?? localize.no_data);
    }

    if (processedColumns.includes(localize.address)) {
      result.push(item.Address ?? localize.no_data);
    }

    if (processedColumns.includes(localize.department)) {
      result.push(item.State?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.city)) {
      result.push(item.City?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getProceeding = (item) => {
    const result = [];

    if (processedColumns.includes(localize.name)) {
      result.push(item.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.code)) {
      result.push(item.Code ?? localize.no_data);
    }

    if (processedColumns.includes(localize.description)) {
      result.push(item.Description ?? localize.no_data);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getPosition = (item) => {
    const result = [];

    if (processedColumns.includes(localize.name)) {
      result.push(item.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.description)) {
      result.push(item.Description ?? localize.no_data);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getPersonal = (item) => {
    const result = [];

    if (processedColumns.includes(localize.identification_type)) {
      result.push(item.IdentificationType ?? localize.no_data);
    }

    if (processedColumns.includes(localize.identification)) {
      result.push(item.Identification ?? localize.no_data);
    }

    if (processedColumns.includes(localize.surName)) {
      result.push(item.SurName ?? localize.no_data);
    }

    if (processedColumns.includes(localize.lastName)) {
      result.push(item.LastName ?? localize.no_data);
    }

    if (processedColumns.includes(localize.position)) {
      result.push(item.Position?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.warehouse)) {
      result.push(item.Warehouse?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.contractor)) {
      result.push(item.Contractor?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.phone_number)) {
      result.push(item.PhoneNumber ?? localize.no_data);
    }

    if (processedColumns.includes(localize.email)) {
      result.push(item.EmailAddress ?? localize.no_data);
    }

    if (processedColumns.includes(localize.address)) {
      result.push(item.Address ?? localize.no_data);
    }

    if (processedColumns.includes(localize.department)) {
      result.push(item.State?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.city)) {
      result.push(item.City?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getPaymentType = (item) => {
    const result = [];

    if (processedColumns.includes(localize.name)) {
      result.push(item.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.default_value)) {
      result.push(
        item.Type === 1
          ? `${item.DefaultValue}%`
          : getValueInCurrency(getCurrencyFromSetup(), item.DefaultValue ?? 0),
      );
    }

    if (processedColumns.includes(localize.need_observation)) {
      result.push(item.NeedObservation ? localize.yed : localize.no);
    }

    if (processedColumns.includes(localize.description)) {
      result.push(item.Description ?? localize.no_data);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getOperationalArea = (item) => {
    const result = [];

    if (processedColumns.includes(localize.name)) {
      result.push(item.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.description)) {
      result.push(item.Description ?? localize.no_data);
    }

    if (processedColumns.includes(localize.background_color)) {
      result.push(item.BackgroundColor ?? localize.no_data);
    }

    if (processedColumns.includes(localize.foreground_color)) {
      result.push(item.ForegroundColor ?? localize.no_data);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getMeasurementUnit = (item) => {
    const result = [];

    if (processedColumns.includes(localize.name)) {
      result.push(item.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.symbol)) {
      result.push(item.Symbol ?? localize.no_data);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getInventoryMovementHeader = (item) => {
    const result = [];

    if (processedColumns.includes(localize.movement_date)) {
      result.push(GetDateInDDMMYYYYFormat(new Date(item.MovementDate)));
    }

    if (processedColumns.includes(localize.code)) {
      result.push(item.Code ?? localize.no_data);
    }

    if (processedColumns.includes(localize.movement_type)) {
      result.push(item.MovementType?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.movement)) {
      result.push(
        getMovementTypeName(item.MovementType?.MovementType) ??
          localize.no_data,
      );
    }

    if (processedColumns.includes(localize.event)) {
      result.push(`${item.EventRelated?.Id ? item.EventRelated?.Code : "-"}`);
    }

    if (processedColumns.includes(localize.warehouse)) {
      result.push(item.Warehouse?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getInventoryMovementType = (item) => {
    const result = [];

    if (processedColumns.includes(localize.name)) {
      result.push(item.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.type)) {
      result.push(getMovementTypeName(item.MovementType));
    }

    if (processedColumns.includes(localize.referenced_by_usage)) {
      result.push(item.referencedByUsage ? localize.yes : localize.no);
    }

    if (processedColumns.includes(localize.notification)) {
      result.push(item.createNotification ? localize.yes : localize.no);
    }

    if (processedColumns.includes(localize.description)) {
      result.push(item.Description ?? localize.no_data);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getEvidenceTemplate = (item) => {
    const result = [];

    if (processedColumns.includes(localize.name)) {
      result.push(item.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.evidences)) {
      result.push(`${getEvidencesCount(item)}`);
    }

    if (processedColumns.includes(localize.description)) {
      result.push(item.Description ?? localize.no_data);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getEventType = (item) => {
    const result = [];

    if (processedColumns.includes(localize.event_type)) {
      result.push(item.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.operational_area)) {
      result.push(item.operationalArea?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.service_value)) {
      result.push(
        getValueInCurrency(getCurrencyFromSetup(), item.executionValue ?? 0),
      );
    }

    if (processedColumns.includes(localize.workflow)) {
      result.push(item.WorkFlow?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.visit_report)) {
      result.push(item.ReviewForm?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.request_signature)) {
      result.push(item.RequestSignature ? localize.yes : localize.no);
    }

    if (processedColumns.includes(localize.notification)) {
      result.push(item.createNotification ? localize.yes : localize.no);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getExecutionStatus = (item) => {
    const result = [];

    if (processedColumns.includes(localize.name)) {
      result.push(item.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.background_color)) {
      result.push(item.BackgroundColor ?? localize.no_data);
    }

    if (processedColumns.includes(localize.foreground_color)) {
      result.push(item.ForegroundColor ?? localize.no_data);
    }

    if (processedColumns.includes(localize.operational_status)) {
      result.push(getOperationalStatusNameFromId(item.OperationalStatus));
    }

    if (processedColumns.includes(localize.is_initial_status)) {
      result.push(item.IsInitial ? localize.yes : localize.no);
    }

    if (processedColumns.includes(localize.is_final_status)) {
      result.push(item.IsFinal ? localize.yes : localize.no);
    }

    if (processedColumns.includes(localize.notification)) {
      result.push(
        !item.notifyByEmail && !item.createNotification
          ? localize.no
          : `${item.notifyByEmail ? localize.email : ""} ${
              item.notifyByEmail && item.createNotification ? " - " : ""
            } ${item.createNotification ? localize.notification : ""}`,
      );
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getDynamicFormField = (item) => {
    const result = [];

    if (processedColumns.includes(localize.field_type)) {
      result.push(getFieldTypeName(item.ValueType));
    }

    if (processedColumns.includes(localize.title)) {
      result.push(item.Title ?? localize.no_data);
    }

    if (processedColumns.includes(localize.help_text)) {
      result.push(item.Description ?? localize.no_data);
    }

    if (processedColumns.includes(localize.visible_on_mobile)) {
      result.push(item.VisibleOnMobile ? localize.yes : localize.no);
    }

    if (processedColumns.includes(localize.mandatory)) {
      result.push(item.Mandatory ? localize.yes : localize.no);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getDynamicForm = (item) => {
    const result = [];

    if (processedColumns.includes(localize.name)) {
      result.push(item.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.description)) {
      result.push(item.Description ?? localize.no_data);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getDailyRoute = (item) => {
    const result = [];

    if (processedColumns.includes(localize.route_date)) {
      result.push(item.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.name)) {
      result.push(item.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.edition_level)) {
      result.push(item.IsPrivate ? localize.is_private : localize.is_public);
    }

    if (processedColumns.includes(localize.assigned_to)) {
      result.push(
        !item.PersonalAssigned?.SurName
          ? localize.no_data
          : `${item.PersonalAssigned.SurName} ${item.PersonalAssigned.LastName}`,
      );
    }

    if (processedColumns.includes(localize.observations)) {
      result.push(item.Observation ?? localize.no_data);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getContractor = (item) => {
    const result = [];

    if (processedColumns.includes(localize.identification_type)) {
      result.push(item.IdentificationType ?? localize.no_data);
    }

    if (processedColumns.includes(localize.identification)) {
      result.push(item.Identification ?? localize.no_data);
    }

    if (processedColumns.includes(localize.name)) {
      result.push(item.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.phone_number)) {
      result.push(item.PhoneNumber ?? localize.no_data);
    }

    if (processedColumns.includes(localize.email)) {
      result.push(item.Email ?? localize.no_data);
    }

    if (processedColumns.includes(localize.contact_name)) {
      result.push(item.ContactName ?? localize.no_data);
    }

    if (processedColumns.includes(localize.address)) {
      result.push(item.Address ?? localize.no_data);
    }

    if (processedColumns.includes(localize.department)) {
      result.push(item.State?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.city)) {
      result.push(item.City?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getClient = (item) => {
    const result = [];

    if (processedColumns.includes(localize.client_type)) {
      result.push(item.ClientType?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.identification_type)) {
      result.push(item.IdentificationType ?? localize.no_data);
    }

    if (processedColumns.includes(localize.identification)) {
      result.push(item.Identification ?? localize.no_data);
    }

    if (processedColumns.includes(localize.name)) {
      result.push(item.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.address)) {
      result.push(
        item.AddressComplement
          ? `${item.Address} ${item.AddressComplement ?? ""}`
          : item.Address ?? localize.no_data,
      );
    }

    if (processedColumns.includes(localize.phone_number)) {
      result.push(item.PhoneNumber ?? localize.no_data);
    }

    if (processedColumns.includes(localize.contact_name)) {
      result.push(item.ContactName ?? localize.no_data);
    }

    if (processedColumns.includes(localize.email)) {
      result.push(item.Email ?? localize.no_data);
    }

    if (processedColumns.includes(localize.department)) {
      result.push(item.State?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.city)) {
      result.push(item.City?.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getAdditionalNewness = (item) => {
    const result = [];

    if (processedColumns.includes(localize.name)) {
      result.push(item.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.need_observation)) {
      result.push(item.NeedObservation ? localize.yes : localize.no);
    }

    if (processedColumns.includes(localize.description)) {
      result.push(item.Description ?? localize.no_data);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const getAdditionalCostTypes = (item, plainValue) => {
    const result = [];

    if (processedColumns.includes(localize.name)) {
      result.push(item.Name ?? localize.no_data);
    }

    if (processedColumns.includes(localize.default_value)) {
      result.push(
        plainValue
          ? item.DefaultValue
          : getValueInCurrency(getCurrencyFromSetup(), item.DefaultValue),
      );
    }

    if (processedColumns.includes(localize.generates_technician_payment)) {
      result.push(
        item.GeneratesPaymentToTechnician ? localize.yes : localize.no,
      );
    }

    if (processedColumns.includes(localize.payment_mode)) {
      result.push(
        item.GeneratesPaymentToTechnician
          ? getPaymentTypeModeLabel(item.PaymentMode)
          : localize.no_data,
      );
    }

    if (processedColumns.includes(localize.value)) {
      result.push(
        item.PaymentMode === "PERCENTAGE"
          ? `${item.PaymentValue} %`
          : plainValue
          ? item.PaymentValue
          : getValueInCurrency(getCurrencyFromSetup(), item.PaymentValue),
      );
    }

    if (processedColumns.includes(localize.notification)) {
      result.push(item.createNotification ? localize.yes : localize.no);
    }

    if (processedColumns.includes(localize.modification_date)) {
      result.push(
        new Date(item.ModificationDate).toLocaleString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (processedColumns.includes(localize.modified_by)) {
      result.push(item.CreatedBy ?? localize.no_data);
    }

    return result;
  };

  const generateCSVData = () => {
    try {
      const items = processDataAccordingToType(data, true);

      if (items && items.length > 0) {
        const separator = getEffectiveSeparator();

        const csvContent = [
          processedColumns.join(separator),
          ...items.map((row) => row.join(separator).replaceAll("\n", "")),
        ].join("\n");

        return csvContent;
      } else {
        setMessageTitle(localize.app_name);
        setMessage(localize.no_data_to_export);
        setShowErrorMessage(true);
        setIsLoading(false);
        return undefined;
      }
    } catch (Exception) {
      console.error(`Error generating CSV: ${report_title}`, Exception);

      setMessageTitle(localize.error_unexpected);
      setMessage(Exception.message);
      setShowErrorMessage(true);
      setIsLoading(false);
      return undefined;
    }
  };

  const downloadCSV = async () => {
    try {
      setIsLoading(true);

      const csvData = generateCSVData();

      if (csvData) {
        const BOM = "\uFEFF";
        const blob = new Blob([BOM + csvData], {
          type: "text/csv;charset=utf-8;",
        });

        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `${report_title ?? localize.app_name}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (Exception) {
      console.error(`Error downloading CSV: ${report_title}`, Exception);

      setMessageTitle(localize.error_unexpected);
      setMessage(Exception.message);
      setShowErrorMessage(true);
    } finally {
      setIsLoading(false);
      closeCallback && closeCallback();
    }
  };

  const fixColumnHeadersOrder = (allColumns, selectedColumns) => {
    const orderMap = new Map();

    allColumns.forEach((value, index) => {
      orderMap.set(value, index);
    });

    const reArrangedColumns = [...selectedColumns].sort(
      (a, b) => orderMap.get(a) - orderMap.get(b),
    );

    setProcessedColumns([...reArrangedColumns]);
  };

  return (
    <Box className="flex flex-col gap-2">
      {showErrorMessage && (
        <ModalBackground
          title={messageTitle}
          showModal={showErrorMessage}
          setShowModal={setShowErrorMessage}
          hideCancelButton
          okButtonText={localize.ok}
          onClickToSubmit={() => {
            setShowErrorMessage(false);
            closeCallback && closeCallback();
          }}
          Component={
            <Box className="p-2">
              <Typography>{message}</Typography>
            </Box>
          }
        />
      )}

      {isLoading && (
        <ModalBackground
          title={localize.app_name}
          showModal={isLoading}
          setShowModal={setIsLoading}
          hideCancelButton
          hideSubmitButton
          Component={<LoadingPanel donotUseHscreen />}
        />
      )}

      {showCsvSetup && (
        <ModalBackground
          title={localize.select_separator}
          showModal={showCsvSetup}
          setShowModal={setShowCsvSetup}
          okButtonText={localize.download}
          onClickToCancel={() => {
            setShowColumnsSelector(false);
            closeCallback && closeCallback();
          }}
          onClickToSubmit={() => {
            setShowColumnsSelector(false);
            downloadCSV();
          }}
          Component={
            <Box className="flex flex-col gap-2 my-2 w-full">
              <Alert severity="info">
                <label className="text-xs">
                  {localize.select_separator_Description}
                </label>
              </Alert>

              <Box className="flex gap-2 w-full justify-evenly">
                <label>
                  <input
                    type="radio"
                    value=","
                    checked={separator === ","}
                    onChange={(e) => setSeparator(e.target.value)}
                  />{" "}
                  {localize.comma_separator}
                </label>

                <label>
                  <input
                    type="radio"
                    value=";"
                    checked={separator === ";"}
                    onChange={(e) => setSeparator(e.target.value)}
                  />{" "}
                  {localize.semicolon_separator}
                </label>

                <label>
                  <input
                    type="radio"
                    value=":"
                    checked={separator === ":"}
                    onChange={(e) => setSeparator(e.target.value)}
                  />{" "}
                  {localize.colon_separator}
                </label>

                <label>
                  <input
                    type="radio"
                    value="tab"
                    checked={separator === "tab"}
                    onChange={(e) => setSeparator(e.target.value)}
                  />{" "}
                  {localize.tab_separator}
                </label>
              </Box>

              <label style={{ paddingLeft: "18px" }}>
                <input
                  type="radio"
                  value="custom"
                  checked={separator === "custom"}
                  onChange={(e) => setSeparator(e.target.value)}
                />{" "}
                {localize.custom}
              </label>
              {separator === "custom" && (
                <input
                  type="text"
                  placeholder={localize.enter_separator}
                  value={customSeparator}
                  maxLength={1}
                  onChange={(e) => setCustomSeparator(e.target.value)}
                  style={{ marginLeft: "28px", padding: "5px" }}
                />
              )}
            </Box>
          }
        />
      )}

      {showColumnsSelector && (
        <ModalBackground
          title={localize.select_columns_for_report}
          showModal={showColumnsSelector}
          setShowModal={setShowColumnsSelector}
          cancelButtonText={localize.close}
          customSecondaryAction={{
            name: localize.CSV,
            action: () => {
              fixColumnHeadersOrder(columns, processedColumns);
              setShowColumnsSelector(false);
              setShowCsvSetup(true);
            },
          }}
          okButtonText={localize.print}
          onClickToCancel={() => {
            setShowColumnsSelector(false);
            closeCallback && closeCallback();
          }}
          onClickToSubmit={() => {
            fixColumnHeadersOrder(columns, processedColumns);
            setShowColumnsSelector(false);

            if (data && data.length > 0) {
              setShowPrinter(true);
            } else {
              setMessage(localize.no_data_to_print);
              setMessageTitle(localize.app_name);
              setShowErrorMessage(true);
            }
          }}
          Component={
            <ColumnsSelector
              columnstoShow={columns}
              columnsProcessed={processedColumns}
              setColumnsProcessed={setProcessedColumns}
            />
          }
          validators={!processedColumns || processedColumns.length === 0}
        />
      )}

      {data && data.length > 0 && showPrinter && (
        <PrinterModal
          show={showPrinter}
          setShow={setShowPrinter}
          modalTitle={title ?? localize.print}
          reportTitle={report_title}
          reportSubtitle={report_subtitle}
          columnHeaders={processedColumns}
          data={processDataAccordingToType(data)}
          closeCallback={closeCallback}
        />
      )}
    </Box>
  );
}
